<script setup lang="ts">
import UIKit from '@pocketprep/ui-kit'
import { computed, onMounted } from 'vue'
import { formatDate } from '@/utils'
import { assignmentModule } from '@/store/assignment/module'
import { examModule } from '@/store/exam/module'
import { instructorModule } from '@/store/instructor/module'
import { organizationModule } from '@/store/organization/module'
import { toastModule } from '@/store/toast/module'
import { bundleModule } from '@/store/bundle/module'
import { analyticsModule } from '@/store/analytics/module'
import { licenseModule } from '@/store/license/module'

const PocketLink = UIKit.Link

const props = defineProps<{
    assignmentId: string
}>()

const assignments = computed(() => {
    return assignmentModule.getters.getCurrentOrgAssignments()
})

const assignment = computed(() => {
    const assignmentId = props.assignmentId
    const storedAssignment = assignments.value[assignmentId]

    return storedAssignment
})

const currentOrg = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const currentExamMetadata = computed(() => {
    const assignmentMetadataId = assignment.value?.examMetadata.objectId
    if (!assignmentMetadataId) {
        return null
    }
    return examMetadata.value[assignmentMetadataId]
})

const allInstructorUsers = computed(() => {
    return instructorModule.getters.getCurrentOrgInstructorUsers()
})

const examName = computed(() => {
    return currentExamMetadata.value?.nativeAppName || 'N/A'
})

const createdBy = computed(() => {
    const instructorUserId = assignment.value?.creator
    if (!instructorUserId || !allInstructorUsers.value) {
        return 'N/A'
    }
    const instructorUser = allInstructorUsers.value[instructorUserId.objectId]
    const fullName = [ instructorUser?.firstName, instructorUser?.lastName?.charAt(0) ].join(' ')

    return fullName
})

const createdAt = computed(() => {
    const assignmentCreatedAtDate = assignment.value?.createdAt
    if (!assignmentCreatedAtDate) {
        return 'N/A'
    }
    const formattedDate = formatDate(new Date(assignmentCreatedAtDate))
    return formatDate(formattedDate)
})

const availableDate = computed(() => {
    const assignmentAvailableDateIso = assignment.value?.availableDate?.iso
    if (!assignmentAvailableDateIso) {
        return 'N/A'
    }
    const formattedAvailableDate = formatDate(new Date(assignmentAvailableDateIso), { useShortMonth: true })
    return formattedAvailableDate
})

const dueDate = computed(() => {
    const assignmentDueDateIso = assignment.value?.dueDate?.iso
    if (!assignmentDueDateIso) {
        return 'N/A'
    }
    const formattedDueDate = formatDate(new Date(assignmentDueDateIso), { useShortMonth: true })
    return formattedDueDate
})

const questionCount = computed(() => {
    return assignment.value?.questionSerials.length || 0
})

const questionDifficulties = computed(() => {
    const difficulties = assignment.value?.config.difficulties
    if (!difficulties) {
        return 'N/A'
    }
    const capitalizedDifficulties = difficulties
        .map(difficulty => (difficulty[0]?.toUpperCase() || '') + difficulty.slice(1).toLowerCase())
    return capitalizedDifficulties.join(', ')
})

const subjectIdsInAssignment = computed(() => {
    return assignment.value?.config.subjectIds
})

const currentExamSubjects = computed(() => {
    return examModule.getters.getCurrentExamSubjects()
})

const bundleBrandLib = computed(() => {
    return bundleModule.getters.getBundleBrandLib()
})

const examBundleLinkName = computed(() => {
    const bundleId = assignment.value
        && bundleModule.getters.getExamBundle(assignment.value.examMetadata.objectId)?.objectId
    if (!bundleId) {
        return 'web'
    }
    return bundleBrandLib.value[bundleId]?.universalLinkName || 'web'
})

const copyLink = async () => {
    const link = `https://pocketprep.com/apps/${examBundleLinkName.value}/assignment?id=${assignment.value?.objectId}`

    await navigator.clipboard.writeText(link)
    toastModule.actions.displayToast({
        title: 'Link copied to clipboard.',
    })

    if (currentOrg.value) {
        analyticsModule.actions.amplitudeTrack('Assignment Link Copied', {
            organizationId: currentOrg.value.objectId,
            hasOrgPurchasedSeats: licenseModule.getters.getCurrentOrgLicenses().length ? true : false,
            examGuid: assignment.value?.examMetadata.objectId
                ? examMetadata.value[assignment.value?.examMetadata.objectId]?.examGuid
                : '',
            bundleId: assignment.value
                && bundleModule.getters.getExamBundle(assignment.value.examMetadata.objectId)?.objectId || '',
            assignedStudentCount: assignment.value?.assignedOrgStudents?.length || 0,
            assignmentName: assignment.value?.name,
        })
    }
}

onMounted(async () => {
    await Promise.all([
        examModule.actions.fetchExamMetadata(),
        assignmentModule.actions.fetchAssignments(),
        instructorModule.actions.fetchCurrentOrgInstructorUsers(),
        organizationModule.actions.fetchCurrentOrganization(),
        bundleModule.actions.fetchBundles(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
    ])
    if (currentExamMetadata.value && currentOrg.value) {
        await examModule.actions.fetchQuestionsByExam({
            compositeKey: currentExamMetadata.value.compositeKey,
            organizationId: currentOrg.value.objectId,
            examMetadataId: currentExamMetadata.value.objectId,
        })
    }
})
</script>

<template>
    <div class="assignment-sidebar" v-if="assignment">
        <div class="assignment-sidebar__sidebar">
            <div class="assignment-sidebar__header">Assignment Details</div>
            <div class="assignment-sidebar__info-row assignment-sidebar__info-row--extra-bottom-margin">
                <div class="assignment-sidebar__info-title">Exam</div>
                <div class="assignment-sidebar__info-value">{{ examName }}</div>
            </div>
            <div class="assignment-sidebar__info-row">
                <div class="assignment-sidebar__info-title">Created By</div>
                <div class="assignment-sidebar__info-value">{{ createdBy }}</div>
            </div>
            <div class="assignment-sidebar__info-row assignment-sidebar__info-row--extra-bottom-margin">
                <div class="assignment-sidebar__info-title">Created On</div>
                <div class="assignment-sidebar__info-value">{{ createdAt }}</div>
            </div>
            <div class="assignment-sidebar__info-row">
                <div class="assignment-sidebar__info-title">Available Date</div>
                <div class="assignment-sidebar__info-value">{{ availableDate }}</div>
            </div>
            <div class="assignment-sidebar__info-row">
                <div class="assignment-sidebar__info-title">Due Date</div>
                <div class="assignment-sidebar__info-value">{{ dueDate }}</div>
            </div>
            <div class="assignment-sidebar__copy-link">
                <PocketLink
                    type="tertiary"
                    :isDarkMode="true"
                    @click="copyLink"
                >
                    Copy Assignment Link
                </PocketLink>
            </div>
            <div class="assignment-sidebar__info-row assignment-sidebar__info-row--extra-bottom-margin">
                <div class="assignment-sidebar__info-title"># of Questions</div>
                <div class="assignment-sidebar__info-value">{{ questionCount }}</div>
            </div>
            <div class="assignment-sidebar__info-row assignment-sidebar__info-row--extra-bottom-margin">
                <div class="assignment-sidebar__info-title">Question Difficulty</div>
                <div class="assignment-sidebar__info-value">{{ questionDifficulties }}</div>
            </div>
            <div class="assignment-sidebar__info-row">
                <div class="assignment-sidebar__info-title">Subjects</div>
                <div class="assignment-sidebar__info-value">
                    {{ subjectIdsInAssignment?.length || 0 }} of {{ Object.keys(currentExamSubjects).length }}
                </div>
            </div>
            <div v-if="subjectIdsInAssignment" class="assignment-sidebar__subjects">
                <div
                    v-for="subjectId in subjectIdsInAssignment"
                    :key="subjectId"
                    class="assignment-sidebar__subject"
                >
                    {{ currentExamSubjects[subjectId]?.name || '' }}
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@use 'sass:color';

.assignment-sidebar {
    width: 320px;
    height: 100%;
    position: fixed;
    box-sizing: border-box;
    top: 54px;
    left: 0;
    background-color: $moonlit-ocean;
    padding: 30px;
    overflow: scroll;
    padding-bottom: 141px;

    &__header {
        text-transform: uppercase;
        color: $fog;
        font-size: 12px;
        line-height: 16px;
        font-weight: 600;
        margin-bottom: 30px;
    }

    &__info-row {
        display: flex;
        justify-content: space-between;

        &--extra-bottom-margin {
            margin-bottom: 16px;
        }
    }

    &__info-title {
        color: $cloudy;
        font-size: 13px;
        line-height: 19px;
        font-weight: 600;
    }

    &__info-value {
        color: $pearl;
        font-size: 14px;
        line-height: 19px;
        font-weight: 500;
    }

    &__copy-link {
        margin: 32px 0;
    }

    &__subjects {
        margin-top: 12px;
        padding: 4px 12px 0;
    }

    &__subject {
        color: $pearl;
        font-size: 14px;
        line-height: 19px;
        margin-bottom: 4px;
    }
}
</style>
